/**
 * Security utilities for authentication and access control
 */

interface LoginAttempt {
  timestamp: number;
  ipAddress: string;
  deviceId: string;
  success: boolean;
  pin?: string; // Only store first 2 digits for logging
}

interface DeviceInfo {
  id: string;
  name: string;
  ipAddress: string;
  trusted: boolean;
  lastSeen: number;
}

interface SecurityConfig {
  maxFailedAttempts: number; // Max failed attempts before lockout
  lockoutDuration: number; // Lockout duration in milliseconds (default: 15 minutes)
  rateLimitWindow: number; // Rate limit window in milliseconds (default: 1 minute)
  maxAttemptsPerWindow: number; // Max attempts per window (default: 5)
  requireTrustedDevice: boolean; // Require device to be trusted
  masterPin: string; // Master PIN for trusted device registration
}

const DEFAULT_CONFIG: SecurityConfig = {
  maxFailedAttempts: 5,
  lockoutDuration: 15 * 60 * 1000, // 15 minutes
  rateLimitWindow: 60 * 1000, // 1 minute
  maxAttemptsPerWindow: 5,
  requireTrustedDevice: true, // Enable device whitelisting by default
  masterPin: import.meta.env.VITE_MASTER_PIN || '9999', // Set via .env
};

class SecurityManager {
  private config: SecurityConfig;
  private loginAttempts: Map<string, LoginAttempt[]> = new Map(); // Key: deviceId
  private lockouts: Map<string, number> = new Map(); // Key: deviceId, Value: unlock timestamp
  private trustedDevices: Map<string, DeviceInfo> = new Map(); // Key: deviceId
  private currentDeviceId: string;

  constructor(config?: Partial<SecurityConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.currentDeviceId = this.getOrCreateDeviceId();
    // Load trusted devices asynchronously (won't block constructor)
    this.loadTrustedDevices().catch(err => {
      console.warn('Failed to load trusted devices on init:', err);
    });
  }

  /**
   * Get or create a unique device ID
   */
  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('pos_device_id');
    if (!deviceId) {
      // Generate a unique device ID based on browser fingerprint
      const fingerprint = this.generateDeviceFingerprint();
      deviceId = `DEV-${Date.now()}-${fingerprint}`;
      localStorage.setItem('pos_device_id', deviceId);
    }
    return deviceId;
  }

  /**
   * Generate a device fingerprint
   */
  private generateDeviceFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('Device fingerprint', 2, 2);
    const canvasHash = canvas.toDataURL().slice(-10);
    
    const screenInfo = `${screen.width}x${screen.height}x${screen.colorDepth}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    return btoa(`${canvasHash}-${screenInfo}-${timezone}`).slice(0, 16);
  }

  /**
   * Get current device ID
   */
  getDeviceId(): string {
    return this.currentDeviceId;
  }

  /**
   * Get IP address (client-side approximation)
   */
  private async getIPAddress(): Promise<string> {
    try {
      // Try to get IP from a service (for logging purposes)
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Check if device is trusted (sync check of local cache)
   */
  isDeviceTrusted(deviceId?: string): boolean {
    const id = deviceId || this.currentDeviceId;
    const device = this.trustedDevices.get(id);
    return device?.trusted === true;
  }

  /**
   * Check if device is trusted (async check from Supabase)
   */
  async isDeviceTrustedAsync(deviceId?: string): Promise<boolean> {
    const id = deviceId || this.currentDeviceId;
    try {
      const { trustedDevicesService } = await import('./database');
      const device = await trustedDevicesService.getByDeviceId(id);
      
      if (device) {
        console.log('🔍 Device found in Supabase:', { deviceId: id, trusted: device.trusted, deviceName: device.deviceName });
        
        if (device.trusted) {
          // Update local cache
          this.trustedDevices.set(id, {
            id: device.deviceId,
            name: device.deviceName || '',
            ipAddress: device.ipAddress || '',
            trusted: device.trusted,
            lastSeen: new Date(device.lastSeen).getTime(),
          });
          return true;
        } else {
          console.warn('⚠️ Device found but not trusted:', id);
          return false;
        }
      } else {
        console.log('❌ Device not found in Supabase:', id);
        // Check local cache as fallback
        return this.isDeviceTrusted(id);
      }
    } catch (error) {
      console.warn('Failed to check device in Supabase, using local cache:', error);
      // Fallback to local cache
      return this.isDeviceTrusted(id);
    }
  }

  /**
   * Register a device as trusted (requires master PIN)
   */
  async registerTrustedDevice(masterPin: string, deviceName?: string): Promise<boolean> {
    // Allow test mode in development (when masterPin is 'TEST_MODE')
    const isTestMode = masterPin === 'TEST_MODE' && (import.meta.env.VITE_APP_ENV === 'development' || !import.meta.env.VITE_APP_ENV);
    
    if (!isTestMode && masterPin !== this.config.masterPin) {
      return false;
    }

    const deviceInfo: DeviceInfo = {
      id: this.currentDeviceId,
      name: deviceName || `Device ${this.currentDeviceId.slice(-8)}`,
      ipAddress: await this.getIPAddress(),
      trusted: true,
      lastSeen: Date.now(),
    };

    this.trustedDevices.set(this.currentDeviceId, deviceInfo);
    await this.saveTrustedDevices();
    return true;
  }

  /**
   * Check if device is locked out
   */
  isLockedOut(deviceId?: string): boolean {
    const id = deviceId || this.currentDeviceId;
    const unlockTime = this.lockouts.get(id);
    
    if (!unlockTime) return false;
    
    if (Date.now() < unlockTime) {
      return true; // Still locked
    }
    
    // Lockout expired, remove it
    this.lockouts.delete(id);
    return false;
  }

  /**
   * Get remaining lockout time in seconds
   */
  getRemainingLockoutTime(deviceId?: string): number {
    const id = deviceId || this.currentDeviceId;
    const unlockTime = this.lockouts.get(id);
    if (!unlockTime) return 0;
    
    const remaining = Math.max(0, unlockTime - Date.now());
    return Math.ceil(remaining / 1000);
  }

  /**
   * Check rate limiting
   */
  private checkRateLimit(deviceId: string): boolean {
    const attempts = this.loginAttempts.get(deviceId) || [];
    const now = Date.now();
    const windowStart = now - this.config.rateLimitWindow;
    
    // Filter attempts within the rate limit window
    const recentAttempts = attempts.filter(a => a.timestamp > windowStart);
    
    if (recentAttempts.length >= this.config.maxAttemptsPerWindow) {
      return false; // Rate limit exceeded
    }
    
    return true;
  }

  /**
   * Record a login attempt
   */
  async recordLoginAttempt(success: boolean, pin?: string): Promise<void> {
    const deviceId = this.currentDeviceId;
    const ipAddress = await this.getIPAddress();
    
    const attempt: LoginAttempt = {
      timestamp: Date.now(),
      ipAddress,
      deviceId,
      success,
      pin: pin ? `${pin.slice(0, 2)}**` : undefined, // Only log first 2 digits
    };

    const attempts = this.loginAttempts.get(deviceId) || [];
    attempts.push(attempt);
    
    // Keep only last 50 attempts per device
    if (attempts.length > 50) {
      attempts.shift();
    }
    
    this.loginAttempts.set(deviceId, attempts);

    // If failed, check for lockout
    if (!success) {
      const failedAttempts = attempts.filter(a => !a.success && a.timestamp > Date.now() - this.config.lockoutDuration);
      
      if (failedAttempts.length >= this.config.maxFailedAttempts) {
        // Lock out the device
        const unlockTime = Date.now() + this.config.lockoutDuration;
        this.lockouts.set(deviceId, unlockTime);
        console.warn(`🔒 Device locked out until ${new Date(unlockTime).toLocaleTimeString()}`);
      }
    } else {
      // Successful login, update device last seen
      const device = this.trustedDevices.get(deviceId);
      if (device) {
        device.lastSeen = Date.now();
        await this.saveTrustedDevices();
      }
    }
  }

  /**
   * Validate login attempt
   */
  async validateLogin(pin: string): Promise<{ allowed: boolean; reason?: string }> {
    // Check if device is locked out
    if (this.isLockedOut()) {
      const remaining = this.getRemainingLockoutTime();
      return {
        allowed: false,
        reason: `Device locked out. Try again in ${Math.ceil(remaining / 60)} minutes.`,
      };
    }

    // Check rate limiting
    if (!this.checkRateLimit(this.currentDeviceId)) {
      return {
        allowed: false,
        reason: 'Too many login attempts. Please wait a moment.',
      };
    }

    // Check if device is trusted (if required) - check Supabase first
    if (this.config.requireTrustedDevice) {
      const isTrusted = await this.isDeviceTrustedAsync();
      if (!isTrusted) {
        return {
          allowed: false,
          reason: 'Device not authorized. Please contact administrator.',
        };
      }
    }

    return { allowed: true };
  }

  /**
   * Get all trusted devices
   */
  getTrustedDevices(): DeviceInfo[] {
    return Array.from(this.trustedDevices.values());
  }

  /**
   * Remove a trusted device
   */
  async removeTrustedDevice(deviceId: string): Promise<void> {
    this.trustedDevices.delete(deviceId);
    try {
      const { trustedDevicesService } = await import('./database');
      await trustedDevicesService.delete(deviceId);
    } catch (error) {
      console.warn('Failed to delete from Supabase, using localStorage:', error);
      await this.saveTrustedDevices();
    }
  }

  /**
   * Save trusted devices to Supabase (with localStorage fallback)
   */
  private async saveTrustedDevices(): Promise<void> {
    try {
      // Try to save to Supabase first
      const { trustedDevicesService } = await import('./database');
      const device = this.trustedDevices.get(this.currentDeviceId);
      
      if (device && this.currentDeviceId) {
        // Use currentDeviceId directly to ensure we have the correct ID
        const deviceId = this.currentDeviceId;
        
        const existing = await trustedDevicesService.getByDeviceId(deviceId);
        if (existing) {
          await trustedDevicesService.update(deviceId, {
            deviceName: device.name,
            ipAddress: device.ipAddress,
            lastSeen: new Date(device.lastSeen).toISOString(),
            trusted: device.trusted,
          });
        } else {
          await trustedDevicesService.create({
            deviceId: deviceId,
            deviceName: device.name,
            ipAddress: device.ipAddress,
            userAgent: navigator.userAgent,
            trusted: device.trusted,
            registeredAt: new Date().toISOString(),
            lastSeen: new Date(device.lastSeen).toISOString(),
          });
        }
      } else {
        console.warn('Cannot save device: device or deviceId is missing', { 
          device: !!device, 
          deviceId: this.currentDeviceId 
        });
      }
    } catch (error) {
      // Fallback to localStorage if Supabase fails
      console.warn('Failed to save to Supabase, using localStorage:', error);
      const devices = Array.from(this.trustedDevices.entries());
      localStorage.setItem('pos_trusted_devices', JSON.stringify(devices));
    }
  }

  /**
   * Load trusted devices from Supabase (with localStorage fallback)
   */
  private async loadTrustedDevices(): Promise<void> {
    // Note: Device whitelisting/trusted devices system is currently disabled
    // If you want to re-enable it, uncomment the code below and migrate to Firebase
    
    /* 
    try {
      // Try to load from Firebase Firestore
      const { trustedDevicesService } = await import('./database');
      const devices = await trustedDevicesService.getAll();
      
      if (devices && devices.length > 0) {
        console.log('📦 Loaded trusted devices from Firestore:', devices.length);
        this.trustedDevices = new Map(
          devices.map(d => [
            d.deviceId,
            {
              id: d.deviceId,
              name: d.deviceName || '',
              ipAddress: d.ipAddress || '',
              trusted: d.trusted,
              lastSeen: new Date(d.lastSeen).getTime(),
            }
          ])
        );
        
        // Check if current device is in the list
        const currentDevice = this.trustedDevices.get(this.currentDeviceId);
        if (currentDevice) {
          console.log('✅ Current device found in Firestore cache:', this.currentDeviceId);
        } else {
          console.log('⚠️ Current device not in Firestore cache:', this.currentDeviceId);
        }
        return;
      }
    } catch (error) {
      console.warn('Failed to load from Firestore, trying localStorage:', error);
    }
    */

    // Fallback to localStorage (if device whitelisting was previously enabled)
    try {
      const stored = localStorage.getItem('pos_trusted_devices');
      if (stored) {
        const devices = JSON.parse(stored) as [string, DeviceInfo][];
        this.trustedDevices = new Map(devices);
        console.log('📦 Loaded trusted devices from localStorage:', devices.length);
        
        // Note: Background migration to Firestore is disabled
        // If you want to re-enable device whitelisting, implement Firebase migration
      }
    } catch (error) {
      console.error('Failed to load trusted devices:', error);
    }
  }

  /**
   * Migrate devices from localStorage to Supabase
   */
  private async migrateToSupabase(devices: [string, DeviceInfo][]): Promise<void> {
    try {
      const { trustedDevicesService } = await import('./database');
      
      for (const [deviceId, device] of devices) {
        // Use the key (deviceId) instead of device.id to ensure consistency
        if (!deviceId) {
          console.warn('Skipping device with no ID:', device);
          continue;
        }
        
        const existing = await trustedDevicesService.getByDeviceId(deviceId);
        if (!existing) {
          await trustedDevicesService.create({
            deviceId: deviceId, // Use the key, not device.id
            deviceName: device.name,
            ipAddress: device.ipAddress,
            userAgent: navigator.userAgent,
            trusted: device.trusted,
            registeredAt: new Date(device.lastSeen).toISOString(),
            lastSeen: new Date(device.lastSeen).toISOString(),
          });
        }
      }
      
      // Clear localStorage after successful migration
      localStorage.removeItem('pos_trusted_devices');
    } catch (error) {
      console.error('Migration failed:', error);
    }
  }

  /**
   * Get login attempt history
   */
  getLoginAttempts(deviceId?: string): LoginAttempt[] {
    const id = deviceId || this.currentDeviceId;
    return this.loginAttempts.get(id) || [];
  }

  /**
   * Clear login attempts (for testing/admin)
   */
  clearLoginAttempts(deviceId?: string): void {
    const id = deviceId || this.currentDeviceId;
    this.loginAttempts.delete(id);
    this.lockouts.delete(id);
  }

  /**
   * Update security configuration
   */
  updateConfig(newConfig: Partial<SecurityConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): SecurityConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const securityManager = new SecurityManager();

// Export types
export type { SecurityConfig, DeviceInfo, LoginAttempt };

